import { IAdvisorPerformance } from "@/DataPages/types/advisorPerformance/model";
import { apiInstance, gql } from "@/services/httpAdapter";
import { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";
import { errorLogger } from "@Common/helpers/logger.ts";
import { IPaginatedResponse } from "@/Common/api/types/pagination";

export async function getAdvisorPerformance(
	locationId: string,
	commissionCycleId: string,
	payPeriodId: string,
	first: number,
	after: string | null = null
) {
	const query = gql`
		query getAdvisorPerformance(
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$pagination: PaginationInput
		) {
			getAdvisorPerformance(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				pagination: $pagination
			) {
				edges {
					node {
						_id
						commissionCycleId
						payPeriodId
						date
						employeeId
						userId
						userFullName
						department
						repairOrderNumber
						repairOrderCount
						discountAmount
						discountPercentage
						subletGross
						totalGross
						customerPay {
							labor {
								amount
								percentage
								hours
							}
							parts {
								amount
								percentage
							}
						}
						warranty {
							labor {
								amount
								percentage
								hours
							}
							parts {
								amount
								percentage
							}
						}
						internal {
							labor {
								amount
								percentage
								hours
							}
							parts {
								amount
								percentage
							}
						}
					}
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		pagination: {
			first,
			after: after ?? "",
		},
	};

	try {
		const response: GraphqlQueryResponse<"getAdvisorPerformance", IPaginatedResponse<IAdvisorPerformance>> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getAdvisorPerformance;
	} catch (err) {
		errorLogger(err as Error, { function: "getAdvisorPerformance", message: "Failed to get advisor performance", variables });
		return null;
	}
}

export async function uploadAdvisorPerformance(locationId: string, payPeriodId: string, file: File) {
	if (!locationId || !payPeriodId || !file) {
		return {
			status: false,
			message: "Invalid parameters",
		};
	}

	const query = gql`
		query getAdvisorPerformancePresignedUrl($locationId: String!, $payPeriodId: String!) {
			getAdvisorPerformancePresignedUrl(locationId: $locationId, payPeriodId: $payPeriodId) {
				fileName
				presignedUrl
			}
		}
	`;

	const variables = { locationId, payPeriodId };

	try {
		const response: GraphqlQueryResponse<"getAdvisorPerformancePresignedUrl", { fileName: string; presignedUrl: string }> =
			await apiInstance.post("/graphql", { query, variables });

		const presigned = response.data?.data?.getAdvisorPerformancePresignedUrl;
		if (!presigned) {
			return {
				status: false,
				message: "Failed to get presigned url",
			};
		}

		const uploadFile = new File([file], presigned?.fileName);
		const options = {
			headers: {
				"Content-Type": file.type,
			},
		};

		const upload = await apiInstance.put(presigned?.presignedUrl, uploadFile, options);
		const SUCCESS_STATUS_CODE = 200;
		if (upload.status === SUCCESS_STATUS_CODE) {
			return {
				status: true,
				message: "Advisor performance uploaded successfully",
			};
		}

		return {
			status: false,
			message: "Failed to upload advisor performance",
		};
	} catch (error) {
		errorLogger(error as Error, {
			function: "uploadAdvisorPerformance",
			message: "Failed to upload advisor performance",
			locationId,
			payPeriodId,
		});
		return {
			status: false,
			message: (error as Error).message,
		};
	}
}
